import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where as firestoreWhere,
  orderBy,
  limit,
  startAfter,
  QueryConstraint,
  DocumentData,
  QueryDocumentSnapshot,
  Timestamp,
  Firestore,
} from "firebase/firestore";
import { db } from "./init";

// 重新導出 where 以便在其他文件中使用
export { firestoreWhere as where };

/**
 * 通用 Firestore 操作服務
 */

// 將 Firestore Timestamp 轉換為 Date（防止循環引用）
export function convertTimestampToDate(data: any, visited: WeakSet<object> = new WeakSet()): any {
  if (data === null || data === undefined) return data;
  
  if (data instanceof Timestamp) {
    return data.toDate();
  }
  
  // 只對對象類型進行循環引用檢查（WeakSet 只能存儲對象）
  if (typeof data === "object" && data !== null) {
    if (visited.has(data)) {
      return data; // 如果已經訪問過，直接返回，避免無限遞歸
    }
    visited.add(data);
  }
  
  if (Array.isArray(data)) {
    return data.map((item) => convertTimestampToDate(item, visited));
  }
  
  if (typeof data === "object" && data !== null) {
    const converted: any = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        converted[key] = convertTimestampToDate(data[key], visited);
      }
    }
    return converted;
  }
  
  return data;
}

/**
 * 獲取單個文檔
 */
export async function getDocument<T>(
  collectionPath: string,
  documentId: string
): Promise<T | null> {
  try {
    const docRef = doc(db, collectionPath, documentId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...convertTimestampToDate(data),
      } as T;
    }
    
    return null;
  } catch (error) {
    console.error(`Error getting document ${collectionPath}/${documentId}:`, error);
    throw error;
  }
}

/**
 * 獲取集合中的所有文檔
 */
export async function getCollection<T>(
  collectionPath: string,
  constraints: QueryConstraint[] = []
): Promise<T[]> {
  try {
    const collectionRef = collection(db, collectionPath);
    const q = constraints.length > 0 
      ? query(collectionRef, ...constraints)
      : query(collectionRef);
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertTimestampToDate(doc.data()),
    })) as T[];
  } catch (error) {
    console.error(`Error getting collection ${collectionPath}:`, error);
    throw error;
  }
}

/**
 * 創建文檔
 */
export async function createDocument<T extends Record<string, any>>(
  collectionPath: string,
  data: Omit<T, "id" | "createdAt" | "updatedAt">
): Promise<string> {
  try {
    const collectionRef = collection(db, collectionPath);
    const docData = {
      ...data,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    
    const docRef = await addDoc(collectionRef, docData);
    return docRef.id;
  } catch (error) {
    console.error(`Error creating document in ${collectionPath}:`, error);
    throw error;
  }
}

/**
 * 更新文檔
 */
export async function updateDocument<T extends Record<string, any>>(
  collectionPath: string,
  documentId: string,
  data: Partial<Omit<T, "id" | "createdAt">>
): Promise<void> {
  try {
    const docRef = doc(db, collectionPath, documentId);
    const updateData = {
      ...data,
      updatedAt: Timestamp.now(),
    };
    
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error(`Error updating document ${collectionPath}/${documentId}:`, error);
    throw error;
  }
}

/**
 * 刪除文檔
 */
export async function deleteDocument(
  collectionPath: string,
  documentId: string
): Promise<void> {
  try {
    const docRef = doc(db, collectionPath, documentId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error(`Error deleting document ${collectionPath}/${documentId}:`, error);
    throw error;
  }
}

/**
 * 分頁查詢
 */
export async function getPaginatedCollection<T>(
  collectionPath: string,
  pageSize: number = 20,
  lastDoc?: QueryDocumentSnapshot<DocumentData>,
  constraints: QueryConstraint[] = []
): Promise<{
  data: T[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}> {
  try {
    const collectionRef = collection(db, collectionPath);
    let q = query(collectionRef, ...constraints, orderBy("createdAt", "desc"), limit(pageSize + 1));
    
    if (lastDoc) {
      q = query(collectionRef, ...constraints, orderBy("createdAt", "desc"), startAfter(lastDoc), limit(pageSize + 1));
    }
    
    const querySnapshot = await getDocs(q);
    const docs = querySnapshot.docs;
    const hasMore = docs.length > pageSize;
    const data = (hasMore ? docs.slice(0, pageSize) : docs).map((doc) => ({
      id: doc.id,
      ...convertTimestampToDate(doc.data()),
    })) as T[];
    
    return {
      data,
      lastDoc: hasMore ? docs[pageSize - 1] : null,
      hasMore,
    };
  } catch (error) {
    console.error(`Error getting paginated collection ${collectionPath}:`, error);
    throw error;
  }
}

